<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import AccountAccessTokensAPI from 'dashboard/api/accountAccessTokens';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  BaseTable,
  BaseTableCell,
  BaseTableRow,
} from 'dashboard/components-next/table';

const MAX_ACCESS_TOKENS = 10;

const { t } = useI18n();
const accessTokens = ref([]);
const isFetching = ref(false);
const isCreating = ref(false);
const isDeleting = ref(false);
const tokenName = ref('');
const createdToken = ref(null);
const selectedToken = ref(null);
const createDialog = ref(null);
const deleteDialog = ref(null);

const isAtLimit = computed(
  () => accessTokens.value.length >= MAX_ACCESS_TOKENS
);

const tableHeaders = computed(() => [
  t('INTEGRATION_SETTINGS.DEVELOPER_API.TABLE.NAME'),
  t('INTEGRATION_SETTINGS.DEVELOPER_API.TABLE.TOKEN'),
  t('INTEGRATION_SETTINGS.DEVELOPER_API.TABLE.CREATED'),
  t('INTEGRATION_SETTINGS.DEVELOPER_API.TABLE.LAST_USED'),
  t('INTEGRATION_SETTINGS.DEVELOPER_API.TABLE.ACTIONS'),
]);

const formatDateTime = value => {
  if (!value) return t('INTEGRATION_SETTINGS.DEVELOPER_API.NEVER');

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const fetchAccessTokens = async () => {
  isFetching.value = true;
  try {
    const response = await AccountAccessTokensAPI.get();
    accessTokens.value = response.data.payload;
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('INTEGRATION_SETTINGS.DEVELOPER_API.FETCH_ERROR')
    );
  } finally {
    isFetching.value = false;
  }
};

const openCreateDialog = () => {
  tokenName.value = '';
  createdToken.value = null;
  createDialog.value?.open();
};

const resetCreateDialog = () => {
  tokenName.value = '';
  createdToken.value = null;
};

const createAccessToken = async () => {
  if (!tokenName.value.trim()) return;

  isCreating.value = true;
  try {
    const response = await AccountAccessTokensAPI.create({
      access_token: { name: tokenName.value.trim() },
    });
    const { token, ...tokenMetadata } = response.data.payload;
    createdToken.value = { ...tokenMetadata, token };
    accessTokens.value = [tokenMetadata, ...accessTokens.value];
    useAlert(t('INTEGRATION_SETTINGS.DEVELOPER_API.CREATE_SUCCESS'));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('INTEGRATION_SETTINGS.DEVELOPER_API.CREATE_ERROR')
    );
  } finally {
    isCreating.value = false;
  }
};

const copyCreatedToken = async () => {
  await copyTextToClipboard(createdToken.value.token);
  useAlert(t('INTEGRATION_SETTINGS.DEVELOPER_API.COPY_SUCCESS'));
};

const closeCreateDialog = () => {
  createDialog.value?.close();
};

const openDeleteConfirmation = token => {
  selectedToken.value = token;
  deleteDialog.value?.open();
};

const resetDeleteDialog = () => {
  selectedToken.value = null;
};

const deleteAccessToken = async () => {
  if (!selectedToken.value) return;

  isDeleting.value = true;
  try {
    await AccountAccessTokensAPI.delete(selectedToken.value.id);
    accessTokens.value = accessTokens.value.filter(
      token => token.id !== selectedToken.value.id
    );
    useAlert(t('INTEGRATION_SETTINGS.DEVELOPER_API.DELETE_SUCCESS'));
    deleteDialog.value?.close();
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) ||
        t('INTEGRATION_SETTINGS.DEVELOPER_API.DELETE_ERROR')
    );
  } finally {
    isDeleting.value = false;
  }
};

onMounted(fetchAccessTokens);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.DEVELOPER_API.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.DEVELOPER_API.TITLE')"
        :description="$t('INTEGRATION_SETTINGS.DEVELOPER_API.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
        title-icon="i-lucide-key-round"
      >
        <template #actions>
          <Button
            :label="$t('INTEGRATION_SETTINGS.DEVELOPER_API.ADD_TOKEN')"
            icon="i-lucide-plus"
            size="sm"
            :disabled="isAtLimit"
            @click="openCreateDialog"
          />
        </template>
        <template #count>
          <span class="text-body-main text-n-slate-11">
            {{
              $t('INTEGRATION_SETTINGS.DEVELOPER_API.COUNT', {
                count: accessTokens.length,
                limit: MAX_ACCESS_TOKENS,
              })
            }}
          </span>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        class="mb-4 flex items-start justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4"
      >
        <div class="flex min-w-0 gap-3">
          <span
            class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-blue-3 text-n-blue-11"
          >
            <span class="i-lucide-info size-4" />
          </span>
          <div>
            <p class="mb-1 text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.DEVELOPER_API.INFO_TITLE') }}
            </p>
            <p class="mb-0 text-sm leading-6 text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.DEVELOPER_API.INFO_DESCRIPTION') }}
            </p>
          </div>
        </div>
        <a
          href="/docs"
          target="_blank"
          rel="noopener noreferrer"
          class="shrink-0 text-sm font-medium text-n-blue-11 hover:underline"
        >
          {{ $t('INTEGRATION_SETTINGS.DEVELOPER_API.VIEW_DOCS') }}
        </a>
      </div>

      <BaseTable
        :headers="tableHeaders"
        :items="accessTokens"
        :no-data-message="$t('INTEGRATION_SETTINGS.DEVELOPER_API.EMPTY_STATE')"
        show-header-when-empty
      >
        <template #row="{ items }">
          <BaseTableRow v-for="token in items" :key="token.id" :item="token">
            <BaseTableCell>
              <p class="mb-0 font-medium text-n-slate-12">
                {{ token.name }}
              </p>
              <p
                v-if="token.created_by"
                class="mb-0 mt-1 text-xs text-n-slate-11"
              >
                {{
                  $t('INTEGRATION_SETTINGS.DEVELOPER_API.CREATED_BY', {
                    name: token.created_by.name,
                  })
                }}
              </p>
            </BaseTableCell>
            <BaseTableCell>
              <code
                class="rounded-md bg-n-alpha-2 px-2 py-1 font-mono text-xs text-n-slate-12"
              >
                {{ token.token_prefix }}
              </code>
            </BaseTableCell>
            <BaseTableCell>
              {{ formatDateTime(token.created_at) }}
            </BaseTableCell>
            <BaseTableCell>
              {{ formatDateTime(token.last_used_at) }}
            </BaseTableCell>
            <BaseTableCell align="end">
              <Button
                v-tooltip.top="$t('INTEGRATION_SETTINGS.DEVELOPER_API.REVOKE')"
                :aria-label="$t('INTEGRATION_SETTINGS.DEVELOPER_API.REVOKE')"
                icon="i-lucide-trash-2"
                color="ruby"
                variant="ghost"
                size="sm"
                @click="openDeleteConfirmation(token)"
              />
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <Dialog
      ref="createDialog"
      :title="
        createdToken
          ? $t('INTEGRATION_SETTINGS.DEVELOPER_API.CREATED_TITLE')
          : $t('INTEGRATION_SETTINGS.DEVELOPER_API.CREATE_TITLE')
      "
      :description="
        createdToken
          ? $t('INTEGRATION_SETTINGS.DEVELOPER_API.CREATED_DESCRIPTION')
          : $t('INTEGRATION_SETTINGS.DEVELOPER_API.CREATE_DESCRIPTION')
      "
      :confirm-button-label="
        $t('INTEGRATION_SETTINGS.DEVELOPER_API.CREATE_BUTTON')
      "
      :disable-confirm-button="!tokenName.trim()"
      :is-loading="isCreating"
      :show-cancel-button="!createdToken"
      :show-confirm-button="!createdToken"
      @confirm="createAccessToken"
      @close="resetCreateDialog"
    >
      <template v-if="createdToken">
        <div class="rounded-lg border border-n-weak bg-n-solid-1 p-4">
          <p
            class="mb-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
          >
            {{ $t('INTEGRATION_SETTINGS.DEVELOPER_API.ACCESS_TOKEN') }}
          </p>
          <div class="flex items-center gap-2">
            <code
              class="min-w-0 flex-1 overflow-x-auto rounded-md bg-n-alpha-2 px-3 py-2 font-mono text-sm text-n-slate-12"
            >
              {{ createdToken.token }}
            </code>
            <Button
              :aria-label="$t('INTEGRATION_SETTINGS.DEVELOPER_API.COPY')"
              icon="i-lucide-copy"
              color="slate"
              variant="faded"
              @click="copyCreatedToken"
            />
          </div>
        </div>
        <Button
          :label="$t('INTEGRATION_SETTINGS.DEVELOPER_API.DONE')"
          class="w-full"
          @click="closeCreateDialog"
        />
      </template>
      <Input
        v-else
        v-model="tokenName"
        :label="$t('INTEGRATION_SETTINGS.DEVELOPER_API.NAME_LABEL')"
        :placeholder="$t('INTEGRATION_SETTINGS.DEVELOPER_API.NAME_PLACEHOLDER')"
        :maxlength="100"
        autofocus
      />
    </Dialog>

    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="$t('INTEGRATION_SETTINGS.DEVELOPER_API.DELETE_TITLE')"
      :description="
        $t('INTEGRATION_SETTINGS.DEVELOPER_API.DELETE_DESCRIPTION', {
          name: selectedToken?.name,
        })
      "
      :confirm-button-label="
        $t('INTEGRATION_SETTINGS.DEVELOPER_API.DELETE_CONFIRM')
      "
      :is-loading="isDeleting"
      @confirm="deleteAccessToken"
      @close="resetDeleteDialog"
    />
  </SettingsLayout>
</template>
